import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { ConfirmationModal } from '@components/ConfirmationModal';
import { useGetAssessmentById } from '@features/assessment';
import { useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CaretRightIcon,
  CheckIcon,
  SpinnerIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useDebounce } from '@uidotdev/usehooks';

// Deep import: the tenant-profile barrel pulls in its screens, which import
// back into shared feature code. Only this hook is wanted here.
import { useApplyManualEdits } from '../../tenant-profile-hub/api/tenant-profile-template-api';
import { useRemediationRun, useSaveRemediationPlaceholders } from '../api/remediation-api';
import { PlaceholderDocumentPreview } from '../components/PlaceholderDocumentPreview';
import { PlaceholderFillCard } from '../components/PlaceholderFillCard';
import { RemediationRunSkeleton } from '../components/RemediationSkeletons';
import { useRunPlaceholders } from '../hooks/useRunPlaceholders';
import {
  groupByDocument,
  hasValue,
  isProfileSavable,
  toProfileValue,
  unfilledPlaceholders,
} from '../utils/placeholders';

/**
 * How long typing has to stop before the values are written.
 *
 * One request per pause rather than one per keystroke. Short enough that
 * leaving the page a second after typing still saves.
 */
const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * The list scrolls, not the dialog.
 *
 * A run can leave dozens of blanks, and letting `DialogContent` take the
 * overflow scrolled the sentence that explains the list off the top of it.
 * Capped against the viewport too, so a short window does not end up with a
 * dialog taller than the screen.
 */
const MODAL_LIST_SX = {
  m: 0,
  pl: 2.5,
  maxHeight: 'min(320px, 40vh)',
  overflowY: 'auto',
} as const;

/**
 * The step between deciding the documents and applying them: filling the blanks
 * the AI could not.
 *
 * A placeholder exists for one reason — the organization profile had no value
 * for it when the document was written. That makes this the only point in the
 * flow where the user is looking at the exact list of facts their profile is
 * missing, with the sentence each one belongs to beside it.
 *
 * WALKED PER DOCUMENT, ANSWERED PER TOKEN. The user thinks in documents, so
 * that is the unit they page through. But the answers are keyed by token, so a
 * `[DPO_CONTACT_EMAIL]` shared by four documents is typed once and reads as
 * filled on all four — paging is navigation, not four copies of one question.
 *
 * EVERY value is saved to the run, and substituted into every document of it
 * when they are rendered at finalize. That is the whole point of the step and
 * it was missing at first: the answers lived in component state, died on
 * navigation, and the downloaded PDF still read `[DPO_CONTACT_EMAIL]`.
 *
 * A value the organization profile has a field for is ALSO written there, as a
 * separate and explicitly confirmed act, so the next run is not asked for it
 * again. One that it has no field for — a procedure number, an issue date —
 * belongs to this run alone and stops there. The card says which is which.
 *
 * FILLING IS ENCOURAGED, NOT REQUIRED. Continue used to wait for every blank on
 * the open document, on the reasoning that `[DPO_CONTACT_EMAIL]` where a
 * commitment should be is not a document anyone can hand to a regulator. True,
 * and beside the point: the user who does not know the DPO's email today cannot
 * conjure it, and a dead button gave them nowhere to go but the close button —
 * losing the eight answers they did have along with the one they did not.
 *
 * So a blank can be skipped, and the cost of skipping is stated rather than
 * enforced. The card says what the sentence will read as, the document beside it
 * shows the token still standing, the last step confirms exactly which ones will
 * ship that way, and the summary says so again before the version is forked.
 * `isBlank` is the single statement of what still counts as unanswered.
 *
 * LEAVING IS ALLOWED TOO. Values autosave, the position is in the URL, and the
 * footer offers the same "Save for later" the review screen does — the step is
 * resumable, so there is no reason to hold anyone in it.
 */
export const RemediationPlaceholdersPage: FC = observer(() => {
  const theme = useTheme();
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  const { data: run } = useRemediationRun(runId);
  const { items, placeholders, isLoading } = useRunPlaceholders(runId);
  const { data: assessment } = useGetAssessmentById({ assessmentId: run?.assessmentId ?? '' });
  const saveProfile = useApplyManualEdits();
  // Every value, profile-backed or not, goes to the run — that is what the
  // renderer substitutes at finalize. Saving to the organization profile is a
  // separate, additional act for the subset the profile has a field for.
  const savePlaceholders = useSaveRemediationPlaceholders(runId ?? '');

  // Keyed by token, not by document — see the note above on why paging must not
  // fork the answer.
  const [values, setValues] = useState<Record<string, string>>({});
  /**
   * Whether the run's stored values have been read into the form.
   *
   * Nothing may be written before this is true. The form starts empty, so an
   * autosave firing first would send `{}` and erase everything the user had
   * already submitted — the page would destroy the work it exists to collect.
   */
  const [hydrated, setHydrated] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** The "these will stay as placeholders" check, at the end of the walk. */
  const [submitWarnOpen, setSubmitWarnOpen] = useState(false);
  // Which blank the document is scrolled to. Set by focusing or clicking a
  // field, so the two panes always agree on what is being edited.
  const [focusedToken, setFocusedToken] = useState<string | null>(null);

  const documents = useMemo(() => groupByDocument(placeholders), [placeholders]);

  /**
   * Which document is open, held in the URL as the review holds `?item=`.
   *
   * The step is resumable — the values are written as they are typed — but
   * resuming at document one of nine after answering eight is its own reason to
   * give up, and a reload used to do exactly that. 1-based in the URL because it
   * is user-facing, and the same number the "Document 3 of 9" line shows.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // Clamped on the way in, so a hand-typed or stale `?doc=` cannot put the page
  // in a state where `isLast` and the paging buttons disagree about where it is.
  const docIndex = Math.min(
    Math.max(0, (Number(searchParams.get('doc')) || 1) - 1),
    Math.max(0, documents.length - 1),
  );
  // Every caller already moves within range; the clamp above is what defends
  // against a hand-typed or stale value.
  const goToDocument = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next === 0) params.delete('doc');
    else params.set('doc', String(next + 1));
    // Replaced, not pushed: paging nine documents should not bury the back
    // button under nine entries the user has to click through to leave.
    setSearchParams(params, { replace: true });
  };

  const current = documents[docIndex];
  /** What the open group is called — the unnamed group is labelled, not identified. */
  const currentName = current?.documentName ?? t('app.remediation.summary.untitledDocument');
  // The document behind the open group, for the preview beside the fields. The
  // unnamed group has no document to show, which is why it is unnamed.
  const currentItem = current?.documentName
    ? (items?.items ?? []).find((i) => i.documentName === current.documentName)
    : undefined;

  /**
   * What a token reads as right now: what the user typed, or the value the
   * profile already holds. Empty means the document still shows the token —
   * the same rule `applyPlaceholderValues` applies at render, so the preview
   * cannot promise something the PDF will not deliver.
   */
  const resolveToken = (token: string): string => {
    const typed = (values[token] ?? '').trim();
    if (typed) return typed;
    const field = placeholders.find((p) => p.token === token)?.field;
    return field && hasValue(field.currentValue) ? String(field.currentValue) : '';
  };
  const isLast = docIndex >= documents.length - 1;

  const backToReview = () => navigate(routes.remediationReview(runId ?? ''));

  /**
   * Autosave, as the review screen does for decisions.
   *
   * Typing four documents' worth of values and losing them to a closed tab is
   * the same loss as typing four decisions and losing those, and the review
   * screen already decided that is unacceptable. Debounced so a save is one
   * request per pause rather than one per keystroke.
   *
   * The whole map goes every time — the endpoint replaces rather than merges,
   * so a cleared value is genuinely cleared. Skipped while a manual save is in
   * flight, and skipped when nothing has changed since the last write, which is
   * what stops the effect saving its own result back on mount.
   */
  const debouncedValues = useDebounce(values, AUTOSAVE_DEBOUNCE_MS);
  const lastSaved = useRef<string>('{}');

  // Show back what was already submitted. Once only — re-running on every poll
  // of the run query would overwrite what the user is typing with the last
  // thing the server saw.
  useEffect(() => {
    if (hydrated || !run) return;
    const stored = run.placeholderValues ?? {};
    lastSaved.current = JSON.stringify(stored);
    setValues(stored);
    setHydrated(true);
  }, [run, hydrated]);

  /**
   * The one writer, for the autosave and for every explicit save.
   *
   * Unchanged values send nothing. Re-submitting an untouched form is not a free
   * no-op: the write bumps the run's `updated_at`, which is the clock the
   * dispatch guard and the stale-run sweep both read.
   *
   * THE MARKER ROLLS BACK ON FAILURE. It used to be set before the request and
   * left set whatever happened, which was the quiet version of the loss this
   * step keeps being redesigned to prevent: a rejected save still recorded the
   * map as written, the autosave then saw nothing to do, and the values sat on
   * screen under an "All values saved" indicator the server had never heard
   * about. Rolling it back means the next change — or the next click — genuinely
   * retries.
   *
   * Rejects when the save fails, so callers can hold their navigation back.
   */
  const write = async (next: Record<string, string>): Promise<void> => {
    if (!runId) return;
    const serialized = JSON.stringify(next);
    if (serialized === lastSaved.current) return;
    const previous = lastSaved.current;
    lastSaved.current = serialized;
    try {
      await savePlaceholders.mutateAsync({ runId, values: next });
    } catch (error) {
      lastSaved.current = previous;
      throw error;
    }
  };

  useEffect(() => {
    if (!runId || !hydrated) return;
    void write(debouncedValues).catch(() => {
      // The mutation's own toast has already said why, and the rolled-back
      // marker means this is retried rather than forgotten.
    });
    // Deliberately keyed on the values alone. `savePlaceholders` is recreated
    // each render, so depending on it would fire this on every render rather
    // than on every change of value. (The exhaustive-deps rule is not enabled
    // in this project, so there is no disable comment to pair with this —
    // adding one is itself a lint error.)
  }, [debouncedValues, runId, hydrated]);

  /**
   * Write what is on screen now — rather than when the debounce next fires — and
   * only then go.
   *
   * This covers the 800 ms between the last keystroke and the autosave: anything
   * that leaves the page in that window would drop the value just typed.
   *
   * A failed save does not navigate. The mutation's error toast has already said
   * what went wrong, and staying put keeps the values on screen to retry with;
   * losing them to a silent failure is how someone ends up filling the same form
   * twice.
   */
  const saveThen = async (go: () => void) => {
    try {
      await write(values);
    } catch {
      return;
    }
    go();
  };

  /** Save, then move on — to the next document, or out of the step. */
  const saveAndAdvance = () =>
    saveThen(() =>
      isLast ? navigate(routes.remediationSummary(runId ?? '')) : goToDocument(docIndex + 1),
    );

  /**
   * Leave with everything written, as the review screen's "Save for later" does.
   *
   * Review offers a way out that is not a decision; this step had none — only
   * "Back to review" and a Continue that used to be dead. The values and the
   * position both survive, so coming back resumes rather than restarts.
   */
  const saveForLater = () => saveThen(() => navigate(routes.remediationList()));

  // Only profile-backed blanks the user actually typed into. A blank input is
  // not an instruction to erase a value the profile already holds.
  //
  // `isProfileSavable` is the same test the card's "saves to profile" chip
  // applies, so the footer never counts a blank the chip called document-only.
  const toSave = placeholders.filter(
    (p) => isProfileSavable(p.field) && (values[p.token] ?? '').trim().length > 0,
  );

  const unfilled = unfilledPlaceholders(placeholders, values);
  // Every blank on THIS document. Says how many are left, no longer whether the
  // walk may go on.
  const unfilledHere = unfilledPlaceholders(current?.placeholders ?? [], values);

  /**
   * Submitting is the last moment a skipped blank can still be filled, so it is
   * the one place the walk asks rather than just tells. Paging between documents
   * does not: it is cheap and reversible, and a dialog on every page turn would
   * rebuild the friction this step just shed.
   */
  const onSubmit = () => {
    if (isLast && unfilled.length > 0) setSubmitWarnOpen(true);
    else void saveAndAdvance();
  };

  const gaps = assessment?.gaps ?? 0;
  const missingDocuments = assessment?.missingDocuments ?? 0;
  const owesSomething = unfilled.length > 0 || gaps > 0 || missingDocuments > 0;

  /**
   * Write the profile-backed answers to the organization profile.
   *
   * ONE REQUEST PER CATEGORY. `PATCH /tenant-profile/manual-edits` saves a
   * single category — it derives the target from the field ids in the body and
   * 422s the whole request if they span more than one. A run's blanks are
   * matched against the entire template, so they land in whatever categories
   * the template keeps them in; sending them together meant every save but a
   * one-category run's was rejected, and nothing at all reached the profile.
   *
   * Sequential, not concurrent: each save mints a new profile version from the
   * current one, and firing them together would have them all branch off the
   * same parent.
   *
   * Keyed by field ID. `ApplyManualEditsService` does `WHERE id IN (...)` on
   * the keys of this map, so sending `field.key` — `reviewer_name` — reached
   * Postgres as a uuid comparison and 500'd with
   * `invalid input syntax for type uuid: "reviewer_name"`.
   */
  const onSave = async () => {
    setConfirmOpen(false);

    const byCategory = new Map<string, Record<string, unknown>>();
    for (const placeholder of toSave) {
      if (!placeholder.field) continue;
      const parsed = toProfileValue(placeholder.field, values[placeholder.token].trim());
      if (!parsed) continue;
      const bucket = byCategory.get(placeholder.field.categoryId) ?? {};
      bucket[placeholder.field.id] = parsed.value;
      byCategory.set(placeholder.field.categoryId, bucket);
    }

    let saved = 0;
    for (const fieldValues of byCategory.values()) {
      try {
        await saveProfile.mutateAsync(fieldValues);
        saved += Object.keys(fieldValues).length;
      } catch {
        // The mutation's own error toast has already said what went wrong.
        // Stop rather than carry on: a category that failed on a locked
        // profile will not be unlocked by the time the next one goes out, and
        // each attempt that does land mints another version.
        break;
      }
    }

    if (saved > 0) {
      enqueueSnackbar(t('app.remediation.placeholders.saved', { count: saved }), {
        variant: 'success',
      });
    }
  };

  if (isLoading) {
    return (
      <Shell>
        <RemediationRunSkeleton />
      </Shell>
    );
  }

  return (
    <Shell>
      <Box
        sx={{
          px: { xs: 2, md: 5 },
          py: 2.5,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h6">{t('app.remediation.placeholders.title')}</Typography>
          {/* Same reassurance the review screen gives, in the same place: the
              values are already written, so leaving does not lose them. */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 12,
              flexShrink: 0,
              color: savePlaceholders.isPending ? theme.palette.ocean[700] : 'text.tertiary',
            }}
          >
            {savePlaceholders.isPending ? (
              <SpinnerIcon size={12} color={theme.palette.ocean[700]} />
            ) : (
              <CheckIcon size={12} color={theme.palette.green[700]} />
            )}
            {t(
              savePlaceholders.isPending
                ? 'app.remediation.placeholders.autosaving'
                : 'app.remediation.placeholders.autosaved',
            )}
          </Box>
        </Box>
        <Typography
          variant="textSM_regular"
          sx={{ color: 'text.secondary', display: 'block', mt: 0.5, maxWidth: 720 }}
        >
          {t('app.remediation.placeholders.subtitle')}
        </Typography>

        {/* Which document is open, and how many are left. The count is the only
            thing that says this is a walk rather than the whole list. */}
        {current && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}
          >
            <Typography variant="textSM_semiBold" sx={{ overflowWrap: 'anywhere' }}>
              {currentName}
            </Typography>
            <CaretRightIcon size={12} color={theme.palette.text.tertiary} />
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              {t('app.remediation.placeholders.documentPosition', {
                index: docIndex + 1,
                total: documents.length,
              })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Fields on one side, the document they change on the other. The
          document is the preview now: a cropped sentence per field could not
          show that the same document names a reviewer twice, or what the clause
          above commits the tenant to. Stacked below `lg`, where two columns
          would leave the prose too narrow to read as prose. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 440px) minmax(0, 1fr)' },
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            px: { xs: 2, md: 3 },
            py: 3,
            borderInlineEnd: { lg: `1px solid ${theme.palette.grey[300]}` },
          }}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 980 }}>
          {documents.length === 0 && (
            <CalloutBanner tone="success" title={t('app.remediation.placeholders.noneTitle')}>
              {t('app.remediation.placeholders.noneBody')}
            </CalloutBanner>
          )}

          {current?.placeholders.map((placeholder) => (
            <PlaceholderFillCard
              key={placeholder.token}
              placeholder={placeholder}
              value={values[placeholder.token] ?? ''}
              active={focusedToken === placeholder.token}
              onFocus={() => setFocusedToken(placeholder.token)}
              onChange={(value) => setValues((v) => ({ ...v, [placeholder.token]: value }))}
            />
          ))}

          {/* Once, on the last document — the tally is whole-run, and repeating
              it on every page would make it read as this document's problem. */}
          {isLast && owesSomething && (
            <CalloutBanner
              tone="warning"
              filledIcon
              title={t('app.remediation.placeholders.warnTitle')}
            >
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {unfilled.length > 0 && (
                  <li>
                    <Typography variant="textSM_regular">
                      {t('app.remediation.placeholders.warnPlaceholders', {
                        count: unfilled.length,
                      })}
                    </Typography>
                  </li>
                )}
                {gaps > 0 && (
                  <li>
                    <Typography variant="textSM_regular">
                      {t('app.remediation.placeholders.warnGaps', { count: gaps })}
                    </Typography>
                  </li>
                )}
                {missingDocuments > 0 && (
                  <li>
                    <Typography variant="textSM_regular">
                      {t('app.remediation.placeholders.warnMissingDocuments', {
                        count: missingDocuments,
                      })}
                    </Typography>
                  </li>
                )}
              </Box>
            </CalloutBanner>
          )}
        </Box>
        </Box>

        {/* Scrolls independently: walking a long document must not carry the
            fields off screen, and vice versa. */}
        <Box
          sx={{
            overflowY: 'auto',
            minHeight: 0,
            backgroundColor: theme.palette.grey[100],
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <PlaceholderDocumentPreview
            item={currentItem}
            tokens={(current?.placeholders ?? []).map((p) => p.token)}
            resolve={resolveToken}
            focusedToken={focusedToken}
          />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 0,
          px: { xs: 2, md: 5 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Two ways out that are not "finish the form": back to the decisions —
            the review screen is where a value that looks wrong gets checked
            against the change that introduced it — and out of the flow
            altogether, with everything written. Paging back sits with the other
            paging on the right. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Button variant="tertiary" startIcon={<ArrowLeftIcon size={16} />} onClick={backToReview}>
            {t('app.remediation.placeholders.backToReview')}
          </Button>
          <Button
            variant="tertiary"
            disabled={savePlaceholders.isPending}
            onClick={() => void saveForLater()}
          >
            {t('app.remediation.review.saveForLater')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          {unfilledHere.length > 0 ? (
            /* A count, not a barrier. It used to be the reason the button beside
               it was dead; now it is a note that these can be come back to. */
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              {t('app.remediation.placeholders.skippable', { count: unfilledHere.length })}
            </Typography>
          ) : (
            toSave.length > 0 && (
              <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                {t('app.remediation.placeholders.readyToSave', { count: toSave.length })}
              </Typography>
            )
          )}
          {docIndex > 0 && (
            <Button variant="tertiary" onClick={() => goToDocument(docIndex - 1)}>
              {t('app.remediation.placeholders.previousDocument')}
            </Button>
          )}
          {toSave.length > 0 && (
            <Button
              variant="secondary"
              disabled={saveProfile.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              {t(
                saveProfile.isPending
                  ? 'app.remediation.placeholders.saving'
                  : 'app.remediation.placeholders.save',
              )}
            </Button>
          )}
          <Button
            variant={isLast ? 'ai' : 'primary'}
            endIcon={isLast ? <CheckIcon size={16} /> : <ArrowRightIcon size={16} />}
            disabled={savePlaceholders.isPending}
            onClick={onSubmit}
          >
            {t(
              savePlaceholders.isPending
                ? 'app.remediation.placeholders.submitting'
                : isLast
                  ? 'app.remediation.placeholders.submit'
                  : 'app.remediation.placeholders.nextDocument',
            )}
          </Button>
        </Box>
      </Paper>

      {/* Writing to the organization profile mints a new profile version that
          every future document is generated from, so it is never a side effect
          of typing — the user is shown exactly which fields will change. */}
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        icon={<WarningIcon size={20} />}
        title={t('app.remediation.placeholders.confirmTitle')}
        content={
          <Box>
            <Typography variant="textSM_regular" sx={{ display: 'block', mb: 1 }}>
              {t('app.remediation.placeholders.confirmBody', { count: toSave.length })}
            </Typography>
            <Box component="ul" sx={MODAL_LIST_SX}>
              {toSave.map((placeholder) => (
                <li key={placeholder.token}>
                  <Typography variant="textSM_regular">
                    {placeholder.field?.label}: {values[placeholder.token].trim()}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        }
        buttons={[
          {
            label: t('app.common.cancel'),
            variant: 'outlined',
            onClick: () => setConfirmOpen(false),
          },
          {
            label: t('app.remediation.placeholders.confirmCta'),
            variant: 'contained',
            onClick: onSave,
          },
        ]}
      />

      {/* Skipping is allowed, so this asks rather than blocks — but it names the
          tokens that will appear verbatim in the finished documents, because a
          user who reads `[DPO_CONTACT_EMAIL]` in a downloaded PDF for the first
          time has been told nothing until it is too late to act on. */}
      <ConfirmationModal
        open={submitWarnOpen}
        onClose={() => setSubmitWarnOpen(false)}
        icon={<WarningIcon size={20} />}
        title={t('app.remediation.placeholders.confirmSkipTitle')}
        /* Built only while open. `content` is a prop, so the whole subtree —
           one row and one plural lookup per blank — was being constructed on
           every keystroke for a dialog nobody was looking at. */
        content={
          submitWarnOpen ? (
          <Box>
            <Typography
              variant="textSM_regular"
              sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
            >
              {t('app.remediation.placeholders.confirmSkipBody', { count: unfilled.length })}
            </Typography>
            <Box component="ul" sx={MODAL_LIST_SX}>
              {unfilled.map((placeholder) => (
                <li key={placeholder.token}>
                  <Typography variant="textSM_regular">
                    {placeholder.label}
                    {/* How many documents it lands in, not which. Naming them
                        turned a list of blanks into a wall of filenames, and the
                        reach of a blank is what the reader is weighing here —
                        the names are on the documents themselves, a click away. */}
                    {placeholder.documents.length > 0 && (
                      <Typography
                        component="span"
                        variant="textSM_regular"
                        sx={{ color: 'text.tertiary' }}
                      >
                        {' '}
                        —{' '}
                        {t('app.remediation.placeholders.inDocuments', {
                          count: placeholder.documents.length,
                        })}
                      </Typography>
                    )}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
          ) : null
        }
        buttons={[
          {
            label: t('app.remediation.placeholders.confirmSkipBack'),
            variant: 'outlined',
            onClick: () => {
              setSubmitWarnOpen(false);
              // Lands on the first document that still has a blank, which is
              // rarely the one being submitted from — sending the user back to
              // a page they have already finished is what makes a warning feel
              // like a dead end. Searched on the click rather than every
              // render, since this is the only thing that ever asks.
              const first = documents.findIndex(
                (group) => unfilledPlaceholders(group.placeholders, values).length > 0,
              );
              if (first >= 0) goToDocument(first);
            },
          },
          {
            label: t('app.remediation.placeholders.confirmSkipCta'),
            variant: 'contained',
            onClick: () => {
              setSubmitWarnOpen(false);
              void saveAndAdvance();
            },
          },
        ]}
      />
    </Shell>
  );
});

const Shell: FC<{ children: React.ReactNode }> = observer(({ children }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      borderRadius: 4,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
));
